import { ArrowLeft, ArrowUpRight, BarChart3, Bell, Bold, BookmarkPlus, CheckCircle, ChevronLeft, ChevronRight, Circle as CircleIcon, Clock3, Crop, Download, Euro, Eye, EyeOff, FileText, Folder, GitBranch, GitMerge, History, ImagePlus, Italic, MessageSquare, Pencil, Plus, RefreshCw, RotateCcw, RotateCw, Search, Sparkles, Square, Trash2, Type, Undo2, Upload, Users, Wand2, X, Zap } from "lucide-react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, streamGuttenbergEvents } from "./api";
import { useAuth } from "../Auth";
import "./Guttenberg.css";
import { aiModeLabel, aiProviderLabel, branchScopedLocks, buildRichPlanSections, collaborationColor, emptyAnalysisDraft, emptyCollaborationState, eventTargetsBranch, extractOutlinePreview, formatTargetPages, getCollaborationDisplayName, getCollaborationSessionId, getStoredActiveBranchId, hasGeneratedReport, imageProcessingKey, isOutlineInstruction, jobActivityDetail, mergeDriveFilePage, mergeJobTrace, nextDriveBreadcrumbs, normalizeAiMode, normalizeAiModes, normalizeAiProviders, normalizeBranchId, normalizeOutlinePreview, parseTargetPages, pendingClarificationQuestions, reportAssistantActionInstructions, reportAssistantActionKey, reportAssistantChangeTargets, shouldAutoGenerateOutline, sourcePathLabel, sourceStatusLabel, storeActiveBranchId, toAnalysisDraft, uploadSourceBatch, visualAssetSourceKey, visualReviewIsCurrent } from "./GuttenbergPageUtils";
import { AiActivityPanel, BranchPanel, CollaborationStatus, CostPanel, DriveSourcePicker, ImageCropModal, PlanReviewPanel, ReadOnlyReportPage, WorkflowStepper } from "./GuttenbergWorkflowPanels";
import { AnalysisPanel, ClarificationQuestionModal, ImageBankPanel, ReportContextPanel } from "./GuttenbergAnalysisPanels";
import { CommentSuggestionDiff, FullReportPreview, OutlineLivePreview, ReportAttributionToggle, ReportOutlinePanel, UserInputQueue, VersionPanel } from "./GuttenbergReportPreview";
import { AUTO_RETOUCH_INSTRUCTION, chartCommentTitle, contextImageSrc, defaultImageCaption, pathCaptionFallback, reportImageSrc } from "./GuttenbergImageAndDiff";
import { changeTypeLabel, chartCommentTargetText, chartTitle, cssEscape, insertMarkdownBlockAfterIndex, replaceMarkdownBlockByIndex } from "./GuttenbergChartMarkdown";
import { useGuttenbergProjectCore } from "./GuttenbergProjectCore";
import { GuttenbergProjectView } from "./GuttenbergProjectView";
import { useGuttenbergJobPolling } from "./GuttenbergProjectStateHooks";

export default function GuttenbergReportPage() {
    const { user } = useAuth();
    return user?.can_use_guttenberg ? <ProjectPage/> : <ReadOnlyReportPage/>;
}

function ProjectPage() {
    const params = useParams();
    const projectId = params.projectId;
    const {
        activeAssistantReview,
        activeBranchId,
        activeBranchIdRef,
        activeClarificationQuestion,
        activeReportAssistantThreadId,
        activeReportTool,
        activeVersion,
        activityClock,
        aiMode,
        aiProvider,
        aiProviderBusy,
        aiProviderInfo,
        analysis,
        analysisDraft,
        analysisDraftDirtyRef,
        analysisRunning,
        analyzeProject,
        answerClarificationQuestion,
        appliedReportAssistantActions,
        applyAnalysisUpdate,
        applyJobUpdate,
        applyLightProjectState,
        applyProjectUpdate,
        approveOutline,
        attributionSaving,
        autoRetouchImage,
        branchName,
        branchState,
        browserNotificationPermission,
        busy,
        changeAiMode,
        changeAiProvider,
        chatMessage,
        clarificationAnswerBusy,
        clarificationQuestions,
        clearDriveSearch,
        clearImageProcessingForComment,
        clearLiveSectionsForJob,
        collaboration,
        collaborationContextRef,
        collaborationSessionRef,
        commentAnchor,
        commentDraft,
        commentMode,
        commentReplies,
        contextAutoIntegrationRef,
        contextDraft,
        contextPanelOpen,
        contextSaving,
        contextUploadBusy,
        costs,
        driveBreadcrumbs,
        driveFiles,
        driveLoading,
        driveQuery,
        driveSelection,
        editMode,
        editingTargetRef,
        enableBrowserQuestionNotifications,
        exportFile,
        exportRunning,
        fastMode,
        fastModeBusy,
        fastModeInfo,
        generateOutline,
        imageEditTarget,
        importSelectedDriveFiles,
        initialTabResolved,
        initialTabResolvedRef,
        insertManualImageFromComposer,
        intakeEvents,
        isActiveJob,
        job,
        jobTrace,
        liveSections,
        load,
        mainTab,
        manualImageCaption,
        manualImageFile,
        manualImageNote,
        mergeFeedback,
        milestoneLabel,
        notifiedQuestionIdsRef,
        openDriveBreadcrumb,
        openDriveFolder,
        operationLocked,
        operationStartingRef,
        outlineApprovalPending,
        outlineDraft,
        outlineInstruction,
        outlinePreview,
        outlineRunning,
        pendingComment,
        prepareVisuals,
        processingCommentIds,
        processingImageKeys,
        processingSectionIds,
        project,
        refineOutline,
        refreshDriveFiles,
        refreshReportAfterImageChange,
        rememberImageComment,
        report,
        reportLoaded,
        reportAssistantThinking,
        reportInstruction,
        reportInstructionBankImages,
        reportInstructionFiles,
        reportInstructionImages,
        reportLivePhase,
        reportLivePhaseRef,
        reportOutlineCollapsed,
        resetAnalysis,
        resetManualImageDraft,
        run,
        runFinalReview,
        saveAnalysis,
        saveContextPanel,
        savingSectionIds,
        searchDriveFiles,
        selectChartForComment,
        selectImageForComment,
        selectImageForEdit,
        selectedBankImage,
        sendChat,
        setActiveAssistantReview,
        setActiveBranchForSession,
        setActiveClarificationQuestion,
        setActiveReportAssistantThreadId,
        setActiveReportTool,
        setActiveVersion,
        setActivityClock,
        setAnalysis,
        setAnalysisRunning,
        setAppliedReportAssistantActions,
        setBranchName,
        setBranchState,
        setBusy,
        setChatMessage,
        setClarificationQuestions,
        setCollaboration,
        setCommentAnchor,
        setCommentDraft,
        setCommentMode,
        setCommentReplies,
        setContextPanelOpen,
        setDriveQuery,
        setEditMode,
        setImageEditTarget,
        setImageProcessingKey,
        setInitialTabResolved,
        setIntakeEvents,
        setJobTrace,
        setLiveSections,
        setMainTab,
        setManualImageCaption,
        setManualImageFile,
        setManualImageNote,
        setMergeFeedback,
        setMilestoneLabel,
        setOutlineDraft,
        setOutlineInstruction,
        setOutlinePreview,
        setOutlineRunning,
        setPendingComment,
        setProcessingCommentIds,
        setProcessingSectionIds,
        setProject,
        setReportAssistantThinking,
        setReportInstruction,
        setReportInstructionBankImages,
        setReportInstructionFiles,
        setReportInstructionImages,
        setReportLivePhase,
        setReportOutlineCollapsed,
        setSavingSectionIds,
        setSectionSaving,
        setSelectedBankImage,
        setStatus,
        setWorkflowCollapsed,
        startAnchoredComment,
        startGeneration,
        startInsertionComment,
        stateReloadTimerRef,
        status,
        submitAndTreatComment,
        submitComment,
        submitOutlineInstruction,
        syncBrief,
        targetPages,
        titleDraft,
        toggleDriveFile,
        toggleGuttenbergAttribution,
        updateAnalysisDraft,
        updateContextDraft,
        updateTargetPagesDraft,
        updateTextCommentAnchor,
        updateTitleDraft,
        updateVisualAssetLabel,
        updateVisualAssetStatus,
        uploadClientLogo,
        uploadContextAttachments,
        uploadFiles,
        uploadLiveContextFiles,
        uploadReportAssistantFiles,
        workflowCollapsed,
    } = useGuttenbergProjectCore(projectId);
    const automaticOutlineAttemptRef = useRef("");
    const workflowNotificationStageRef = useRef("");
    const comments = report?.manifest?.comments ?? [];
    const sectionVersions = [...(report?.manifest?.section_versions ?? [])]
        .sort((left, right) => Date.parse(right.created_at) - Date.parse(left.created_at));

    async function applyOpenComments(sectionId, commentIds) {
            setBusy(true);
            setStatus("Traitement des commentaires...");
            const selectedComments = commentIds?.length
                ? comments.filter((comment) => commentIds.includes(comment.id))
                : comments.filter((comment) => comment.status === "open" || comment.status === "suggestion");
            const sectionsToProcess = new Set();
            if (sectionId)
                sectionsToProcess.add(sectionId);
            selectedComments.forEach((comment) => {
                if (comment.section_id)
                    sectionsToProcess.add(comment.section_id);
            });
            if (sectionsToProcess.size) {
                setProcessingSectionIds((current) => {
                    const next = { ...current };
                    sectionsToProcess.forEach((id) => {
                        next[id] = true;
                    });
                    return next;
                });
            }
            try {
                const createdJob = await api.applyComments(projectId, { section_id: sectionId, comment_ids: commentIds, branch_id: activeBranchIdRef.current });
                applyJobUpdate(createdJob);
                if (createdJob.status === "completed" || createdJob.status === "failed") {
                    setBusy(false);
                    await load();
                }
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
                setProcessingSectionIds((current) => {
                    const next = { ...current };
                    sectionsToProcess.forEach((id) => {
                        delete next[id];
                    });
                    return next;
                });
                setBusy(false);
            }
        }

    async function dismissComment(commentId) {
            await run("Commentaire ignore...", async () => {
                await api.updateCommentStatus(projectId, commentId, "dismissed", activeBranchIdRef.current);
            });
        }

    async function suggestComment(commentId) {
            setBusy(true);
            setStatus("Preparation de la suggestion IA...");
            setProcessingCommentIds((current) => ({ ...current, [commentId]: true }));
            try {
                const createdJob = await api.suggestComment(projectId, commentId, activeBranchIdRef.current);
                applyJobUpdate(createdJob);
                await load();
                if (createdJob.status === "completed" || createdJob.status === "failed") {
                    setBusy(false);
                    await load();
                }
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
                setProcessingCommentIds((current) => {
                    const next = { ...current };
                    delete next[commentId];
                    return next;
                });
                setBusy(false);
            }
        }

    async function suggestOpenComments() {
            const targets = comments.filter((comment) => comment.status === "open" && comment.section_id);
            if (!targets.length)
                return;
            setBusy(true);
            setStatus(`Preparation de ${targets.length} suggestion(s)...`);
            setProcessingCommentIds((current) => {
                const next = { ...current };
                targets.forEach((comment) => {
                    next[comment.id] = true;
                });
                return next;
            });
            try {
                for (const comment of targets) {
                    await api.suggestComment(projectId, comment.id, activeBranchIdRef.current);
                }
                await load();
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
                setProcessingCommentIds((current) => {
                    const next = { ...current };
                    targets.forEach((comment) => {
                        delete next[comment.id];
                    });
                    return next;
                });
                setBusy(false);
            }
        }

    async function acceptSuggestion(commentId) {
            await run("Application de la suggestion...", async () => {
                await api.acceptCommentSuggestion(projectId, commentId, activeBranchIdRef.current);
            });
        }

    async function rejectSuggestion(commentId) {
            await run("Suggestion refusee...", async () => {
                await api.rejectCommentSuggestion(projectId, commentId, activeBranchIdRef.current);
            });
        }

    function appendJobTrace(title, message = "", status = "running", sectionId = "") {
            if (!title)
                return;
            setJobTrace((current) => mergeJobTrace(current, [
                {
                    title,
                    message,
                    status,
                    section_id: sectionId,
                    created_at: new Date().toISOString()
                }
            ]));
        }

    function isImageEditJob(nextJob) {
            return nextJob?.type === "comment-suggestion" && nextJob?.result?.target_type === "image_edit";
        }

    function isBlockingJob(nextJob) {
            return isActiveJob(nextJob) && !isImageEditJob(nextJob);
        }

    function currentCollaborationPayload(overrides = {}) {
            const editingTarget = editingTargetRef.current;
            const context = collaborationContextRef.current;
            const tab = context.mainTab ?? mainTab;
            const section = editingTarget?.sectionId
                ? report?.manifest.sections.find((item) => item.id === editingTarget.sectionId)
                : null;
            return {
                session_id: collaborationSessionRef.current,
                display_name: getCollaborationDisplayName(),
                color: collaborationColor(collaborationSessionRef.current),
                mode: editingTarget?.editing ? "editing" : context.commentMode ? "commenting" : context.editMode ? "editing-ready" : "viewing",
                tab,
                section_id: editingTarget?.sectionId ?? null,
                section_title: section?.title ?? "",
                block_index: editingTarget?.blockIndex ?? null,
                editing: Boolean(editingTarget?.editing),
                branch_id: context.activeBranchId ?? activeBranchIdRef.current ?? null,
                ...overrides
            };
        }

    async function sendCollaborationHeartbeat(overrides = {}) {
            try {
                const state = await api.collaborationHeartbeat(projectId, currentCollaborationPayload(overrides));
                setCollaboration(state);
            }
            catch {
                // Presence is opportunistic; editing and report actions keep working without it.
            }
        }

    function focusEditableBlock(sectionId, blockIndex) {
            editingTargetRef.current = { sectionId, blockIndex, editing: true };
            sendCollaborationHeartbeat();
        }

    function blurEditableBlock(sectionId, blockIndex) {
            const current = editingTargetRef.current;
            if (current?.sectionId === sectionId && current?.blockIndex === blockIndex) {
                editingTargetRef.current = { sectionId, blockIndex, editing: false };
                window.setTimeout(() => {
                    if (editingTargetRef.current?.sectionId === sectionId && !editingTargetRef.current?.editing) {
                        editingTargetRef.current = null;
                    }
                    sendCollaborationHeartbeat();
                }, 300);
            }
        }

    function focusReportComment(comment) {
            if (!comment?.section_id)
                return;
            setMainTab("report");
            setActiveReportTool("comments");
            window.setTimeout(() => {
                const block = Number.isFinite(comment.block_index)
                    ? document.querySelector(`[data-section-id="${cssEscape(comment.section_id)}"][data-markdown-block-index="${comment.block_index}"]`)
                    : null;
                const section = block?.closest(".paper-section") ?? document.querySelector(`[data-section-id="${cssEscape(comment.section_id)}"]`);
                const target = block || section;
                target?.scrollIntoView({ behavior: "smooth", block: "center" });
                if (target) {
                    target.classList.add("attention-pulse");
                    window.setTimeout(() => target.classList.remove("attention-pulse"), 1600);
                }
            }, 80);
        }

    function focusAssistantReviewTarget(target, position, total) {
            if (!target?.sectionId)
                return;
            setMainTab("report");
            if (target.versionId && target.blockId)
                setActiveVersion({ versionId: target.versionId, blockId: target.blockId });
            else
                setActiveVersion(null);
            window.setTimeout(() => {
                const exactBlock = target.versionId && target.blockId
                    ? document.querySelector(`[data-assistant-change-version-id="${cssEscape(target.versionId)}"][data-assistant-change-block-id="${cssEscape(target.blockId)}"]`)
                    : null;
                const indexedBlock = Number.isFinite(target.blockIndex)
                    ? document.querySelector(`[data-section-id="${cssEscape(target.sectionId)}"][data-markdown-block-index="${target.blockIndex}"]`)
                    : null;
                const section = document.querySelector(`.paper-section[data-section-id="${cssEscape(target.sectionId)}"]`);
                const element = exactBlock || indexedBlock || section;
                element?.scrollIntoView({ behavior: "smooth", block: "center" });
                if (element) {
                    element.classList.add("attention-pulse");
                    window.setTimeout(() => element.classList.remove("attention-pulse"), 1600);
                }
            }, 100);
            setStatus(`Modification ${position + 1}/${total} - ${target.sectionTitle || target.sectionId}`);
        }

    function reviewAssistantMessageChanges(message, requestedIndex = 0) {
            const versions = [...(report?.manifest?.section_versions ?? [])].sort((left, right) => Date.parse(right.created_at) - Date.parse(left.created_at));
            const targets = reportAssistantChangeTargets(message?.action, versions);
            if (!targets.length) {
                setStatus("Aucune modification localisable pour cette reponse.");
                return;
            }
            const index = Math.max(0, Math.min(requestedIndex, targets.length - 1));
            setActiveAssistantReview({ messageId: message.id, action: message.action, index });
            focusAssistantReviewTarget(targets[index], index, targets.length);
        }

    function navigateAssistantReview(message, offset) {
            const targets = reportAssistantChangeTargets(message?.action, sectionVersions);
            if (!targets.length)
                return;
            const currentIndex = activeAssistantReview?.messageId === message.id ? activeAssistantReview.index : 0;
            const index = Math.max(0, Math.min(currentIndex + offset, targets.length - 1));
            setActiveAssistantReview({ messageId: message.id, action: message.action, index });
            focusAssistantReviewTarget(targets[index], index, targets.length);
        }

    function closeAssistantReview() {
            setActiveAssistantReview(null);
            setActiveVersion(null);
            setStatus("");
        }

    async function cancelCurrentJob() {
            if (!job || !isActiveJob(job))
                return;
            setStatus("Arret demande...");
            try {
                const cancelled = await api.cancelJob(projectId, job.id);
                applyJobUpdate(cancelled);
                setBusy(false);
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Arret impossible");
            }
        }

    async function submitCommentReply(commentId) {
            const content = (commentReplies[commentId] ?? "").trim();
            if (!content)
                return;
            await run("Reponse ajoutee au commentaire...", async () => {
                await api.replyComment(projectId, commentId, content, activeBranchIdRef.current);
                setCommentReplies((current) => ({ ...current, [commentId]: "" }));
            });
        }

    async function submitReportInstruction(event) {
            event.preventDefault();
            const typedMessage = reportInstruction.trim();
            const imageFiles = [...reportInstructionImages];
            const sourceFiles = [...reportInstructionFiles];
            const bankImages = [...reportInstructionBankImages];
            const hasAttachments = imageFiles.length || sourceFiles.length || bankImages.length;
            if (!typedMessage && !hasAttachments)
                return;
            const message = typedMessage || "J'ai joint des fichiers a ce message. Analyse-les pour le rapport.";
            setReportInstruction("");
            setReportInstructionImages([]);
            setReportInstructionFiles([]);
            setReportInstructionBankImages([]);
            setReportAssistantThinking(true);
            setStatus("Assistant rapport...");
            let sourceFileIds = [];
            try {
                sourceFileIds = await uploadReportAssistantFiles(sourceFiles);
                const attachmentIds = await uploadContextAttachments(imageFiles, "Image jointe au message assistant rapport.");
                const response = await api.reportAssistantMessage(
                    projectId,
                    message,
                    attachmentIds,
                    bankImages.map((image) => image.path),
                    sourceFileIds,
                    activeReportAssistantThreadId,
                    activeBranchIdRef.current
                );
                if (response?.project) {
                    setProject(response.project);
                }
                if (response?.message?.thread_id) {
                    setActiveReportAssistantThreadId(response.message.thread_id);
                }
                setStatus(response?.message?.action?.kind === "apply_report_instruction" ? "Proposition prete a confirmer." : "Reponse assistant recue.");
            }
            catch (error) {
                setReportInstruction(typedMessage);
                setReportInstructionImages(imageFiles);
                setReportInstructionFiles(sourceFileIds.length ? [] : sourceFiles);
                setReportInstructionBankImages(bankImages);
                setStatus(error instanceof Error ? error.message : "Erreur");
            }
            finally {
                setReportAssistantThinking(false);
            }
        }

    async function resetReportAssistant() {
            setStatus("Reinitialisation de l'assistant rapport...");
            try {
                const updated = await api.resetReportAssistant(projectId);
                setProject(updated);
                const thread = updated.report_assistant_threads?.[0];
                setActiveReportAssistantThreadId(thread?.id || "");
                setReportInstruction("");
                setReportInstructionImages([]);
                setReportInstructionFiles([]);
                setReportInstructionBankImages([]);
                setAppliedReportAssistantActions({});
                setStatus("Conversation effacee. L'assistant est pret.");
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
            }
        }

    async function applyReportAssistantAction(action) {
            const instructions = reportAssistantActionInstructions(action);
            const instruction = String(action?.instruction ?? "").trim() || instructions.map((item, index) => `${index + 1}. ${item}`).join("\n");
            if (!instruction)
                return;
            const actionKey = reportAssistantActionKey(action);
            if (appliedReportAssistantActions[actionKey]) {
                setStatus("Proposition deja envoyee au rapport.");
                return;
            }
            if (operationLocked()) {
                setStatus("Operation Guttenberg deja en cours.");
                return;
            }
            operationStartingRef.current = true;
            setBusy(true);
            setStatus("Application de la proposition...");
            setMainTab("report");
            setJobTrace([]);
            try {
                const createdJob = await api.applyReportInstruction(
                    projectId,
                    instruction,
                    Array.isArray(action?.attachment_ids) ? action.attachment_ids : [],
                    Array.isArray(action?.bank_image_paths) ? action.bank_image_paths : [],
                    Array.isArray(action?.source_file_ids) ? action.source_file_ids : [],
                    action?.branch_id || activeBranchIdRef.current,
                    {
                        assistantThreadId: action?.assistant_thread_id || activeReportAssistantThreadId,
                        userPrompt: action?.user_prompt || instruction,
                        createRestoreMilestone: true,
                        instructions,
                        targetSectionIds: Array.isArray(action?.target_section_ids) ? action.target_section_ids : [],
                        changeScope: action?.change_scope === "structure" ? "structure" : "content"
                    }
                );
                setAppliedReportAssistantActions((current) => ({ ...current, [actionKey]: true }));
                applyJobUpdate(createdJob);
                await load();
                if (createdJob.status === "completed" || createdJob.status === "failed" || createdJob.status === "cancelled") {
                    setBusy(false);
                    await load();
                }
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
                setBusy(false);
            }
            finally {
                operationStartingRef.current = false;
            }
        }

    async function createMilestone() {
            if (!report?.manifest.sections.length)
                return;
            const label = milestoneLabel.trim() || `Jalon ${new Date().toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}`;
            await run("Sauvegarde du jalon...", async () => {
                await api.createMilestone(projectId, { label });
                setMilestoneLabel("");
            });
        }

    async function restoreMilestone(milestoneId) {
            setActiveVersion(null);
            await run("Restauration du jalon...", async () => {
                await api.restoreMilestone(projectId, milestoneId);
                setLiveSections({});
            });
        }

    async function restoreSectionVersion(version) {
            setActiveVersion(null);
            await run("Restauration de la section...", async () => {
                await api.restoreSectionVersion(projectId, version.section_id, version.id);
                setLiveSections({});
            });
        }

    async function restoreBlockVersion(version, block) {
            setActiveVersion(null);
            await run("Restauration du bloc...", async () => {
                await api.restoreBlockVersion(projectId, version.id, block.id);
                setLiveSections({});
            });
        }

    async function createBranch() {
            const name = branchName.trim();
            if (!name)
                return;
            setActiveVersion(null);
            setMergeFeedback(null);
            await run("Creation de la branche...", async () => {
                const state = await api.createBranch(projectId, { name, switch: true, source_branch_id: activeBranchIdRef.current });
                const nextBranchId = state?.active_branch_id ?? activeBranchIdRef.current;
                setActiveBranchForSession(nextBranchId);
                setBranchState({ ...state, active_branch_id: nextBranchId });
                setBranchName("");
                setLiveSections({});
                await load(nextBranchId);
            }, { skipReload: true, preserveStatus: true });
            setStatus("Branche creee et active.");
        }

    async function switchBranch(branchId) {
            if (!branchId || branchId === activeBranchIdRef.current)
                return;
            setActiveVersion(null);
            setMergeFeedback(null);
            setActiveBranchForSession(branchId);
            await run("Changement de branche...", async () => {
                const state = await api.switchBranch(projectId, branchId);
                setBranchState({ ...state, active_branch_id: branchId });
                setLiveSections({});
                await load(branchId);
            }, { skipReload: true, preserveStatus: true });
            setStatus("Branche chargee.");
        }

    async function mergeBranch(branchId, strategy = "safe") {
            if (!branchId || branchId === activeBranchIdRef.current)
                return;
            setActiveVersion(null);
            await run(strategy === "source" ? "Merge force..." : "Merge de la branche...", async () => {
                const state = await api.mergeBranch(projectId, branchId, strategy, activeBranchIdRef.current);
                setBranchState({ ...state, active_branch_id: activeBranchIdRef.current });
                setLiveSections({});
                const sourceBranch = branchState?.branches?.find((branch) => branch.id === branchId);
                const sourceName = sourceBranch?.name ?? branchId;
                if (state?.merge?.status === "conflict") {
                    const count = state.merge.conflicts?.length ?? 0;
                    const message = `Conflit de merge: ${count} section(s). Utilise Forcer si tu veux garder la branche source.`;
                    setMergeFeedback({
                        status: "conflict",
                        sourceBranchId: branchId,
                        sourceName,
                        targetBranchId: activeBranchIdRef.current,
                        conflicts: state.merge.conflicts ?? [],
                        appliedSections: []
                    });
                    setStatus(message);
                    return;
                }
                await load(activeBranchIdRef.current);
                const appliedSections = state?.merge?.applied_sections ?? [];
                if (state?.merge?.status === "noop" || !appliedSections.length) {
                    setMergeFeedback({
                        status: "noop",
                        sourceBranchId: branchId,
                        sourceName,
                        targetBranchId: activeBranchIdRef.current,
                        conflicts: [],
                        appliedSections: []
                    });
                    setStatus("Aucune modification a merger depuis cette branche.");
                    return;
                }
                setMergeFeedback({
                    status: "merged",
                    sourceBranchId: branchId,
                    sourceName,
                    targetBranchId: activeBranchIdRef.current,
                    conflicts: state?.merge?.conflicts ?? [],
                    appliedSections
                });
                setStatus(strategy === "source" ? "Merge force applique." : "Merge applique.");
            }, { skipReload: true, preserveStatus: true });
        }

    async function savePreviewMarkdown(sectionId, nextMarkdown, origin) {
            const currentMarkdown = liveSections[sectionId] ?? report?.sections[sectionId] ?? "";
            if (!currentMarkdown.trim())
                return;
            if (nextMarkdown === currentMarkdown)
                return;
            setSavingSectionIds((current) => ({ ...current, [sectionId]: true }));
            setLiveSections((current) => ({ ...current, [sectionId]: nextMarkdown }));
            setStatus("Enregistrement de l'edition...");
            try {
                await api.updateSection(projectId, sectionId, nextMarkdown, origin, "Edition directe depuis la preview.", activeBranchIdRef.current);
                await load(activeBranchIdRef.current);
                setLiveSections((current) => {
                    const next = { ...current };
                    delete next[sectionId];
                    return next;
                });
                setStatus("Edition enregistree.");
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
                await load(activeBranchIdRef.current);
            }
            finally {
                setSavingSectionIds((current) => {
                    const next = { ...current };
                    delete next[sectionId];
                    return next;
                });
            }
        }

    async function savePreviewBlock(sectionId, blockIndex, nextBlockMarkdown, origin) {
            const currentMarkdown = liveSections[sectionId] ?? report?.sections[sectionId] ?? "";
            if (!currentMarkdown.trim())
                return;
            await savePreviewMarkdown(
                sectionId,
                replaceMarkdownBlockByIndex(currentMarkdown, blockIndex, nextBlockMarkdown),
                origin,
            );
        }

    async function insertPreviewBlock(sectionId, blockIndex, blockMarkdown, origin) {
            const currentMarkdown = liveSections[sectionId] ?? report?.sections[sectionId] ?? "";
            if (!currentMarkdown.trim())
                return;
            await savePreviewMarkdown(
                sectionId,
                insertMarkdownBlockAfterIndex(currentMarkdown, blockIndex, blockMarkdown),
                origin,
            );
        }

    async function deletePreviewBlock(sectionId, blockIndex, label) {
            const blockLabel = label || "ce bloc";
            if (!window.confirm(`Supprimer ${blockLabel} ?`))
                return;
            await savePreviewBlock(sectionId, blockIndex, "", `Suppression de ${blockLabel}`);
        }

    async function createReportSection(afterSectionId, title) {
            await run("Ajout de la partie...", async () => {
                await api.createSection(projectId, title, afterSectionId, activeBranchIdRef.current);
                setLiveSections({});
            });
        }

    async function deleteReportSection(section) {
            if (!section || !window.confirm(`Supprimer toute la partie « ${section.title} » et son contenu ?`))
                return;
            await run("Suppression de la partie...", async () => {
                await api.deleteSection(projectId, section.id, activeBranchIdRef.current);
                setLiveSections({});
            });
        }

    function startBankImageDiscussion(image) {
            setMainTab("report");
            setReportInstructionBankImages((current) => (current.some((item) => item.path === image.path)
                ? current
                : [...current, image]));
            setActiveReportTool("assistant");
            setStatus("Image ajoutee au message Assistant rapport depuis la banque. Complete la demande puis envoie.");
        }

    function toggleReportAssistantBankImage(image) {
            setReportInstructionBankImages((current) => (current.some((item) => item.path === image.path)
                ? current.filter((item) => item.path !== image.path)
                : [...current, image]));
        }

    function openReportAssistantImageBank() {
            setMainTab("report");
            setActiveReportTool("images");
            setStatus("Selectionne les images a joindre au message Assistant rapport.");
        }

    function openReportAssistantComposer() {
            setMainTab("report");
            setActiveReportTool("assistant");
            setStatus(reportInstructionBankImages.length
                ? `${reportInstructionBankImages.length} image(s) prete(s) a envoyer a l'Assistant rapport.`
                : "Assistant rapport.");
        }

    async function insertSelectedBankImageFromComposer() {
            if (!pendingComment || pendingComment.targetType !== "insertion" || !selectedBankImage)
                return;
            const sectionId = pendingComment.sectionId;
            const imageKey = imageProcessingKey(sectionId, `insert:${pendingComment.blockIndex ?? "end"}`);
            setImageProcessingKey(imageKey, true);
            setSectionSaving(sectionId, true);
            setStatus("Insertion de l'image selectionnee...");
            try {
                const updatedManifest = await api.applySectionImage(projectId, pendingComment.sectionId, {
                    image_path: selectedBankImage.path,
                    caption: manualImageCaption.trim() || selectedBankImage.caption,
                    block_index: pendingComment.blockIndex,
                    user_label: manualImageNote.trim(),
                    branch_id: activeBranchIdRef.current
                });
                setPendingComment(null);
                setCommentAnchor(null);
                setCommentDraft("");
                resetManualImageDraft();
                await refreshReportAfterImageChange(sectionId, updatedManifest, activeBranchIdRef.current);
                setStatus("Image de la banque inseree.");
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
            }
            finally {
                setImageProcessingKey(imageKey, false);
                setSectionSaving(sectionId, false);
            }
        }

    async function replaceImageWithUpload(sectionId, imagePath, file, caption, userLabel) {
            const imageKey = imageProcessingKey(sectionId, imagePath);
            setImageProcessingKey(imageKey, true);
            setSectionSaving(sectionId, true);
            setStatus("Remplacement de l'image...");
            try {
                const updatedManifest = await api.insertSectionImage(projectId, sectionId, file, caption?.trim() || defaultImageCaption(file), null, userLabel?.trim() || "Remplacement manuel en mode edition.", imagePath, activeBranchIdRef.current);
                await refreshReportAfterImageChange(sectionId, updatedManifest, activeBranchIdRef.current);
                setStatus("Image remplacee.");
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
            }
            finally {
                setImageProcessingKey(imageKey, false);
                setSectionSaving(sectionId, false);
            }
        }

    function openImageCropEditor(sectionId, imagePath, caption) {
            setImageEditTarget({ sectionId, imagePath, caption: caption || pathCaptionFallback(imagePath) });
        }

    async function applyImageCrop(file, caption) {
            if (!imageEditTarget)
                return;
            const target = imageEditTarget;
            setImageEditTarget(null);
            await replaceImageWithUpload(target.sectionId, target.imagePath, file, caption || target.caption, "Edition image manuelle.");
        }

    async function replaceImageWithBank(sectionId, imagePath) {
            if (!selectedBankImage)
                return;
            const imageKey = imageProcessingKey(sectionId, imagePath);
            setImageProcessingKey(imageKey, true);
            setSectionSaving(sectionId, true);
            setStatus("Remplacement par l'image selectionnee...");
            try {
                const updatedManifest = await api.applySectionImage(projectId, sectionId, {
                    image_path: selectedBankImage.path,
                    caption: selectedBankImage.caption,
                    replace_path: imagePath,
                    user_label: "Remplacement manuel depuis la banque d'images.",
                    branch_id: activeBranchIdRef.current
                });
                await refreshReportAfterImageChange(sectionId, updatedManifest, activeBranchIdRef.current);
                setStatus("Image remplacee par la selection.");
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
            }
            finally {
                setImageProcessingKey(imageKey, false);
                setSectionSaving(sectionId, false);
            }
        }

    useEffect(() => {
            analysisDraftDirtyRef.current = false;
            initialTabResolvedRef.current = false;
            setInitialTabResolved(false);
            const storedBranchId = getStoredActiveBranchId(projectId);
            setActiveBranchForSession(storedBranchId);
            load(storedBranchId);
        }, [projectId]);

    useEffect(() => {
            collaborationContextRef.current = {
                mainTab,
                editMode,
                commentMode,
                activeBranchId
            };
        }, [mainTab, editMode, commentMode, activeBranchId]);

    useEffect(() => {
            const sessionId = collaborationSessionRef.current;
            sendCollaborationHeartbeat();
            const timer = window.setInterval(() => sendCollaborationHeartbeat(), 8000);
            return () => {
                window.clearInterval(timer);
                api.collaborationLeave(projectId, sessionId).catch(() => {});
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [projectId]);

    useEffect(() => {
            sendCollaborationHeartbeat();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [mainTab, editMode, commentMode, activeBranchId]);

    useEffect(() => {
            reportLivePhaseRef.current = reportLivePhase;
        }, [reportLivePhase]);

    useEffect(() => {
            if (mainTab === "report") {
                setWorkflowCollapsed(true);
            }
        }, [mainTab]);

    useEffect(() => {
            const threads = project?.report_assistant_threads ?? [];
            if (!threads.length)
                return;
            if (!activeReportAssistantThreadId || !threads.some((thread) => thread.id === activeReportAssistantThreadId)) {
                const latest = [...threads].sort((a, b) => Date.parse(b.updated_at || b.created_at) - Date.parse(a.updated_at || a.created_at))[0];
                if (latest?.id)
                    setActiveReportAssistantThreadId(latest.id);
            }
        }, [project, activeReportAssistantThreadId]);

    useEffect(() => {
            if (browserNotificationPermission !== "granted" || typeof window === "undefined" || !("Notification" in window))
                return undefined;
            const notifyUnseenQuestions = () => {
                const pageIsAway = typeof document === "undefined" || document.hidden || !document.hasFocus();
                if (!pageIsAway)
                    return;
                const unseen = clarificationQuestions.filter((question) => question?.id && !notifiedQuestionIdsRef.current.has(question.id));
                unseen.forEach((question) => {
                    notifiedQuestionIdsRef.current.add(question.id);
                    const notification = new window.Notification(question.title || "Guttenberg attend votre réponse", {
                        body: question.prompt || "Une question est disponible dans le rapport.",
                        tag: `guttenberg-question-${projectId}-${question.id}`,
                    });
                    notification.onclick = () => {
                        window.focus();
                        setMainTab("analysis");
                        setActiveClarificationQuestion(question);
                        notification.close();
                    };
                });
            };
            notifyUnseenQuestions();
            document.addEventListener("visibilitychange", notifyUnseenQuestions);
            window.addEventListener("blur", notifyUnseenQuestions);
            return () => {
                document.removeEventListener("visibilitychange", notifyUnseenQuestions);
                window.removeEventListener("blur", notifyUnseenQuestions);
            };
        }, [browserNotificationPermission, clarificationQuestions, projectId]);

    useEffect(() => {
            if (!project || !analysis)
                return;
            const eligible = shouldAutoGenerateOutline({
                project,
                analysis,
                clarificationQuestions,
                report,
                reportLoaded,
                job,
                busy,
                analysisRunning,
                outlineRunning,
            });
            if (!eligible)
                return;
            const fingerprint = [
                analysis.updated_at || "",
                analysis.visual_user_review_corpus_fingerprint || analysis.visual_user_review_source_fingerprint || "",
                project.source_revision || 0,
                project.context_revision || 0,
            ].join("|");
            if (!fingerprint || automaticOutlineAttemptRef.current === fingerprint)
                return;
            automaticOutlineAttemptRef.current = fingerprint;
            setStatus("Analyse terminée. Génération automatique du plan...");
            void generateOutline();
        }, [
            analysis,
            analysisRunning,
            busy,
            clarificationQuestions,
            generateOutline,
            job,
            outlineRunning,
            project,
            report,
            reportLoaded,
            setStatus,
        ]);

    useEffect(() => {
            if (!project || !analysis)
                return;
            const activeJob = job && ["queued", "running"].includes(job.status) ? job : null;
            const jobStages = {
                "prepare-visuals": ["images_running", "Extraction des images", "Guttenberg extrait les visuels des sources."],
                "prepare-analysis": ["analysis_running", "Analyse en cours", "Guttenberg analyse les sources et les données."],
                "generate-outline": ["outline_running", "Plan en cours", "Guttenberg prépare automatiquement le plan."],
                "generate-report": ["writing_running", "Rédaction en cours", "Les sections du rapport sont en cours de rédaction."],
            };
            let stage;
            if (activeJob) {
                stage = jobStages[activeJob.type]
                    || (String(activeJob.type || "").includes("review")
                        ? ["review_running", "Relecture en cours", "Guttenberg relit et corrige le rapport."]
                        : ["operation_running", "Traitement Guttenberg", activeJob.message || "Une opération est en cours."]);
            }
            else if (clarificationQuestions.length) {
                stage = ["questions", "Questions disponibles", "Guttenberg attend vos réponses."];
            }
            else if ((report?.manifest?.review_rounds ?? []).length) {
                stage = ["export", "Rapport prêt", "La relecture est terminée et les exports sont disponibles."];
            }
            else if (hasGeneratedReport(report)) {
                stage = ["corrections", "Rédaction terminée", "Le rapport est prêt pour la relecture et les corrections."];
            }
            else if (analysis.outline_approved) {
                stage = ["writing_ready", "Plan validé", "La rédaction peut maintenant être lancée."];
            }
            else if (analysis.outline_stage === "draft") {
                stage = ["outline_review", "Plan prêt", "Le plan automatique est prêt à être relu et validé."];
            }
            else if (project.report_ready && visualReviewIsCurrent(analysis)) {
                stage = ["analysis_ready", "Analyse terminée", "Le dossier d'analyse est prêt; le plan va être généré automatiquement."];
            }
            else if (analysis.visual_inventory_status === "ready") {
                stage = ["images_review", "Images extraites", "Sélectionnez et annotez les images avant de lancer l'analyse."];
            }
            else {
                stage = ["sources_brief", "Sources et brief", "Ajoutez les sources et précisez l'objectif du rapport."];
            }
            const previousStage = workflowNotificationStageRef.current;
            workflowNotificationStageRef.current = stage[0];
            if (!previousStage || previousStage === stage[0] || stage[0] === "questions")
                return;
            if (browserNotificationPermission !== "granted" || typeof window === "undefined" || !("Notification" in window))
                return;
            if (!(document.hidden || !document.hasFocus()))
                return;
            const notification = new window.Notification(stage[1], {
                body: stage[2],
                tag: `guttenberg-stage-${projectId}-${stage[0]}`,
            });
            notification.onclick = () => {
                window.focus();
                setMainTab(stage[0].startsWith("writing") || stage[0].startsWith("outline") || ["corrections", "export"].includes(stage[0]) ? "report" : "analysis");
                notification.close();
            };
        }, [
            analysis,
            browserNotificationPermission,
            clarificationQuestions,
            job,
            project,
            projectId,
            report,
            setMainTab,
        ]);

    useEffect(() => {
            const events = streamGuttenbergEvents(projectId, (update) => {
                const targetsCurrentBranch = eventTargetsBranch(update, activeBranchIdRef.current);
                if (update.type === "collaboration_state" && update.state) {
                    setCollaboration(update.state);
                }
                if ((update.type === "branch_state_changed" || update.type === "branch_merged") && update.state) {
                    const currentBranchId = activeBranchIdRef.current || "main";
                    setBranchState({ ...update.state, active_branch_id: currentBranchId });
                    if (update.type === "branch_merged" &&
                        (update.target_branch_id === currentBranchId || update.source_branch_id === currentBranchId)) {
                        setActiveVersion(null);
                        setLiveSections({});
                        load(currentBranchId);
                    }
                }
                if (update.type === "section_updated") {
                    const updateBranchId = update.branch_id || "main";
                    const currentBranchId = activeBranchIdRef.current || "main";
                    if (updateBranchId === currentBranchId) {
                        setLiveSections((current) => ({
                            ...current,
                            [update.section_id]: update.markdown ?? current[update.section_id] ?? ""
                        }));
                        setStatus(update.origin ? `Section mise a jour: ${update.origin}` : "Section mise a jour.");
                        load(currentBranchId).then(() => {
                            setLiveSections((current) => {
                                const next = { ...current };
                                delete next[update.section_id];
                                return next;
                            });
                        });
                    }
                }
                if (update.type === "report_restored") {
                    if ((activeBranchIdRef.current || "main") === "main") {
                        setLiveSections({});
                        setStatus(update.origin ? `Rapport restaure: ${update.origin}` : "Rapport restaure.");
                        load("main");
                    }
                }
                if (update.type === "section_started") {
                    setReportLivePhase("writing");
                    setOutlineRunning(false);
                    setLiveSections((current) => ({ ...current, [update.section_id]: "" }));
                    setStatus(`Redaction en cours: ${update.title ?? update.section_id}`);
                    appendJobTrace("Section demarree", update.title ?? update.section_id ?? "", "running", update.section_id ?? "");
                }
                if (update.type === "comment_revision_started" && targetsCurrentBranch) {
                    if (update.section_id) {
                        setProcessingSectionIds((current) => ({ ...current, [update.section_id]: true }));
                    }
                    setStatus(`Traitement des commentaires: ${update.title ?? update.section_id}`);
                }
                if (update.type === "comment_revision_delta" && targetsCurrentBranch) {
                    setStatus(`Revision en cours: ${update.section_id}`);
                }
                if (update.type === "comment_revision_completed" && targetsCurrentBranch) {
                    if (update.section_id) {
                        setProcessingSectionIds((current) => {
                            const next = { ...current };
                            delete next[update.section_id];
                            return next;
                        });
                    }
                    setLiveSections((current) => ({
                        ...current,
                        [update.section_id]: update.markdown ?? current[update.section_id] ?? ""
                    }));
                }
                if (update.type === "comment_suggestion_started" && targetsCurrentBranch) {
                    if (update.comment_id) {
                        setProcessingCommentIds((current) => ({ ...current, [update.comment_id]: true }));
                        if (update.job?.result?.target_type === "image_edit") {
                            rememberImageComment(
                                update.comment_id,
                                update.job.result.section_id,
                                update.job.result.image_path
                            );
                        }
                    }
                    setStatus("Suggestion IA en cours...");
                }
                if ((update.type === "comment_suggestion_completed" ||
                    update.type === "comment_suggestion_failed" ||
                    update.type === "comment_suggestion_accepted" ||
                    update.type === "comment_suggestion_rejected") && targetsCurrentBranch) {
                    const commentId = update.comment?.id ?? update.comment_id ?? update.job?.result?.comment_id;
                    if (commentId) {
                        setProcessingCommentIds((current) => {
                            const next = { ...current };
                            delete next[commentId];
                            return next;
                        });
                    }
                    if (commentId || update.comment?.target_type === "image_edit" || update.job?.result?.target_type === "image_edit") {
                        clearImageProcessingForComment(
                            commentId,
                            update.comment?.section_id ?? update.job?.result?.section_id ?? "",
                            update.comment?.image_path ?? update.job?.result?.image_path ?? ""
                        );
                    }
                    if (update.job)
                        applyJobUpdate(update.job);
                    setBusy(false);
                    setStatus(update.job?.message ?? "Commentaires mis a jour.");
                    load();
                }
                if (update.type === "outline_planning_started") {
                    setReportLivePhase("planning");
                    setOutlineRunning(true);
                    setOutlineDraft("");
                    setOutlinePreview([]);
                    setStatus(update.message ?? "Planification du rapport...");
                }
                if (update.type === "outline_preparation_step") {
                    setReportLivePhase("planning");
                    setStatus(update.message ?? "Preparation du plan...");
                }
                if (update.type === "outline_planning_delta") {
                    setOutlineDraft((current) => {
                        const next = `${current}${update.delta ?? ""}`;
                        setOutlinePreview(extractOutlinePreview(next));
                        return next;
                    });
                }
                if (update.type === "outline_planning_completed") {
                    setOutlineRunning(false);
                    setReportLivePhase("idle");
                    if (update.raw_outline) {
                        setOutlineDraft(update.raw_outline);
                        setOutlinePreview(extractOutlinePreview(update.raw_outline));
                    }
                    if (update.sections) {
                        setOutlinePreview(normalizeOutlinePreview(update.sections));
                    }
                    setStatus("Plan du rapport termine.");
                }
                if (update.type === "intake_started") {
                    setAnalysisRunning(true);
                    setMainTab("analysis");
                    // The analysis owns the next clarification batch. Do not keep
                    // interactive ids from the preceding workspace revision alive.
                    setClarificationQuestions([]);
                    setActiveClarificationQuestion(null);
                    setIntakeEvents([update.message ?? "Analyse demarree."]);
                    setStatus(update.message ?? "");
                }
                if (update.type === "intake_step") {
                    setIntakeEvents((current) => [...current, update.message ?? "Etape d'analyse."]);
                    setStatus(update.message ?? "");
                }
                if (update.type === "intake_completed") {
                    setAnalysisRunning(false);
                    setIntakeEvents((current) => [...current, update.message ?? "Analyse terminée."]);
                    setStatus(update.message ?? "");
                    if (update.project) {
                        applyProjectUpdate(update.project);
                    }
                    if (Array.isArray(update.clarification_questions) && update.clarification_questions.length) {
                        setClarificationQuestions(update.clarification_questions);
                        setActiveClarificationQuestion(update.clarification_questions[0]);
                    }
                    load();
                }
                if (update.type === "intake_interrupted") {
                    setAnalysisRunning(false);
                    setIntakeEvents((current) => [...current, update.message ?? "Analyse interrompue."]);
                    setStatus(update.message ?? "Analyse interrompue; la reprise utilisera le dernier checkpoint.");
                    setBusy(false);
                    if (update.project) {
                        applyProjectUpdate(update.project);
                    }
                    load();
                }
                if (update.type === "intake_failed") {
                    setAnalysisRunning(false);
                    setIntakeEvents((current) => [...current, update.message ?? "Analyse echouee."]);
                    setStatus(update.message ?? "Analyse echouee.");
                    setBusy(false);
                    load();
                }
                if (update.type === "review_started" || update.type === "review_step" || update.type === "review_completed") {
                    setStatus(update.message ?? "Relecture finale...");
                    appendJobTrace(
                        update.type === "review_completed" ? "Relecture terminee" : "Relecture",
                        update.message ?? "",
                        update.type === "review_completed" ? "completed" : "running"
                    );
                }
                if (update.type === "blueprint_support_started" || update.type === "blueprint_support_completed") {
                    setStatus(update.message ?? "Verification des informations avant redaction...");
                    appendJobTrace(
                        "Preparation factuelle",
                        update.message ?? "",
                        update.type === "blueprint_support_completed" ? "completed" : "running"
                    );
                }
                if (update.type === "clarification_patch_completed" || update.type === "clarification_patch_failed") {
                    if (update.job)
                        applyJobUpdate(update.job);
                    setStatus(update.job?.message ?? (update.type === "clarification_patch_completed"
                        ? "Reponse appliquee a la section concernee."
                        : "Le patch cible n'a pas abouti; le rapport actuel est conserve."));
                    appendJobTrace(
                        "Reponse utilisateur",
                        update.job?.message ?? "",
                        update.type === "clarification_patch_completed" ? "completed" : "failed"
                    );
                    load();
                }
                if (update.type === "analysis_clarifications_available") {
                    const questions = Array.isArray(update.clarification_questions)
                        ? [...update.clarification_questions].sort((left, right) => Number(Boolean(right.blocking)) - Number(Boolean(left.blocking)))
                        : [];
                    setClarificationQuestions(questions);
                    if (questions.length)
                        setActiveClarificationQuestion(questions[0]);
                    setStatus(update.message ?? "Quelques precisions peuvent affiner le rapport.");
                    appendJobTrace(
                        update.blocking_count ? "Arbitrage utilisateur requis" : "Questions optionnelles",
                        update.message ?? "",
                        update.blocking_count ? "waiting" : "running"
                    );
                }
                if (update.type === "analysis_resume_started") {
                    if (update.job)
                        applyJobUpdate(update.job);
                    setAnalysisRunning(true);
                    setBusy(true);
                    setStatus(update.message ?? "Reprise de l'analyse...");
                }
                if (update.type === "analysis_resume_deferred") {
                    setStatus(update.message ?? "La reprise de l'analyse reste a relancer.");
                }
                if (update.type === "context_integration_started") {
                    if (update.job)
                        applyJobUpdate(update.job);
                    setBusy(true);
                    setStatus(update.message ?? "Integration des nouveaux inputs en cours.");
                }
                if (update.type === "context_integration_completed") {
                    setStatus(update.message ?? "Nouveaux inputs integres.");
                    setBusy(false);
                    load();
                }
                if (update.type === "context_integration_deferred") {
                    setStatus(update.message ?? "Les nouveaux inputs restent a integrer.");
                    setProject((current) => current ? { ...current, context_integration_status: "pending" } : current);
                }
                if (update.type === "job_progress_note") {
                    setStatus(update.message ?? "");
                    appendJobTrace("Note", update.message ?? "", "running");
                }
                if (update.type === "job_trace" && targetsCurrentBranch) {
                    if (update.job)
                        applyJobUpdate(update.job);
                    if (Array.isArray(update.trace))
                        setJobTrace((current) => mergeJobTrace(current, update.trace));
                }
                if (update.type === "visual_request_started") {
                    setStatus(`Preparation du visuel: ${update.title ?? update.section_id}`);
                    appendJobTrace("Visuel demande", update.title ?? update.section_id ?? "", "running", update.section_id ?? "");
                }
                if (update.type === "visual_request_resolved") {
                    setStatus(`Visuel pret: ${update.title ?? update.path ?? ""}`);
                    appendJobTrace("Visuel pret", update.title ?? update.path ?? "", "completed", update.section_id ?? "");
                }
                if (update.type === "visual_request_completed") {
                    setLiveSections((current) => ({
                        ...current,
                        [update.section_id]: update.markdown ?? current[update.section_id] ?? ""
                    }));
                }
                if (update.type === "section_delta") {
                    setLiveSections((current) => ({
                        ...current,
                        [update.section_id]: `${current[update.section_id] ?? ""}${update.delta ?? ""}`
                    }));
                }
                if (update.type === "section_completed" && targetsCurrentBranch) {
                    setLiveSections((current) => ({
                        ...current,
                        [update.section_id]: update.markdown ?? current[update.section_id] ?? ""
                    }));
                    if (update.job)
                        applyJobUpdate(update.job);
                    appendJobTrace("Section terminee", update.job?.current_step ?? update.section_id ?? "", "completed", update.section_id ?? "");
                }
                if ((update.type === "job_progress" ||
                    update.type === "job_started" ||
                    update.type === "job_completed" ||
                    update.type === "job_failed" ||
                    update.type === "job_cancelled" ||
                    update.type === "comments_job_started" ||
                    update.type === "comments_job_completed" ||
                    update.type === "comments_job_failed" ||
                    update.type === "comments_job_cancelled") && targetsCurrentBranch) {
                    if (update.job)
                        applyJobUpdate(update.job);
                    setStatus(update.job?.message ?? "");
                    if (update.type === "job_started") {
                        appendJobTrace("Job demarre", update.job?.message ?? "", "running");
                    }
                    if (update.type === "job_completed" || update.type === "comments_job_completed") {
                        appendJobTrace("Job termine", update.job?.message ?? "", "completed");
                    }
                    if (update.type === "job_failed" || update.type === "comments_job_failed") {
                        appendJobTrace("Job echoue", update.job?.message ?? "", "failed");
                    }
                    if (update.type === "job_cancelled" || update.type === "comments_job_cancelled") {
                        appendJobTrace("Job annule", update.job?.message ?? "", "cancelled");
                    }
                    if (update.type === "job_completed" ||
                        update.type === "job_failed" ||
                        update.type === "job_cancelled" ||
                        update.type === "comments_job_completed" ||
                        update.type === "comments_job_failed" ||
                        update.type === "comments_job_cancelled") {
                        if (update.type === "job_completed" || update.type === "job_failed" || update.type === "job_cancelled") {
                            setReportLivePhase("idle");
                        }
                        if (update.type === "comments_job_completed" ||
                            update.type === "comments_job_failed" ||
                            update.type === "comments_job_cancelled") {
                            setProcessingSectionIds({});
                            if (update.type === "comments_job_cancelled")
                                setProcessingCommentIds({});
                        }
                        if (update.type === "job_cancelled") {
                            setProcessingSectionIds({});
                            const commentId = update.job?.result?.comment_id;
                            if (typeof commentId === "string") {
                                setProcessingCommentIds((current) => {
                                    const next = { ...current };
                                    delete next[commentId];
                                    return next;
                                });
                            }
                            else {
                                setProcessingCommentIds({});
                            }
                            if (update.job?.result?.target_type === "image_edit") {
                                clearImageProcessingForComment(
                                    typeof commentId === "string" ? commentId : "",
                                    update.job.result.section_id,
                                    update.job.result.image_path
                                );
                            }
                        }
                        if (update.job?.result?.target_type === "image_edit") {
                            clearImageProcessingForComment(
                                update.job.result.comment_id,
                                update.job.result.section_id,
                                update.job.result.image_path
                            );
                        }
                        setBusy(false);
                        clearLiveSectionsForJob(update.job);
                        load();
                    }
                }
                if (update.type === "report_outline") {
                    setReportLivePhase("writing");
                    setOutlineRunning(false);
                    setOutlinePreview([]);
                    setOutlineDraft("");
                    appendJobTrace("Plan charge", `${update.sections?.length ?? 0} section(s) a rediger.`, "completed");
                    load();
                }
                if (update.type === "analysis_started") {
                    const duringWriting = reportLivePhaseRef.current === "writing";
                    setAnalysisRunning(!duringWriting);
                    if (!duringWriting)
                        setMainTab("analysis");
                    setIntakeEvents((current) => [
                        ...current,
                        duringWriting ? "Préparation de la rédaction." : update.message ?? "Analyse initiale."
                    ]);
                    setStatus(duringWriting ? "Préparation de la rédaction..." : update.message ?? "Analyse initiale.");
                    if (duringWriting)
                        appendJobTrace("Preparation", update.message ?? "Analyse du dossier avant redaction.", "running");
                }
                if (update.type === "analysis_delta") {
                    setIntakeEvents((current) => [...current.slice(-8), update.message ?? "Analyse..."]);
                    setStatus(reportLivePhaseRef.current === "writing" ? `Preparation: ${update.message ?? ""}` : update.message ?? "Analyse...");
                    if (reportLivePhaseRef.current === "writing")
                        appendJobTrace("Preparation", update.message ?? "", "running");
                }
                if (update.type === "analysis_artifact") {
                    const payload = update.payload;
                    if (payload?.project_id) {
                        applyAnalysisUpdate(payload);
                    }
                    else if (update.artifact_type) {
                        setIntakeEvents((current) => [...current.slice(-8), `Artefact analyse: ${update.artifact_type}`]);
                    }
                }
                if (update.type === "artifact_created" && update.artifact_type) {
                    setIntakeEvents((current) => [...current.slice(-8), `Artefact: ${update.artifact_type}`]);
                }
                if (update.type === "agent_started") {
                    setIntakeEvents((current) => [...current.slice(-8), `${update.agent} (${update.intelligence})`]);
                    setStatus(reportLivePhaseRef.current === "writing" && update.agent !== "ReportWriterAgent"
                        ? `Preparation: ${update.agent} en cours...`
                        : `${update.agent} en cours...`);
                    if (reportLivePhaseRef.current === "writing")
                        appendJobTrace("Agent", `${update.agent} (${update.intelligence})`, "running");
                }
                if (update.type === "blueprint_pass_started") {
                    setStatus(update.message ?? `${update.agent ?? "Blueprint"} en cours...`);
                    appendJobTrace("Blueprint", update.message ?? update.agent ?? "", "running");
                }
                if (update.type === "blueprint_pass_completed") {
                    setStatus(update.message ?? "Blueprint redactionnel mis a jour.");
                    appendJobTrace(
                        "Blueprint",
                        `${update.message ?? update.agent ?? "Passe terminee"} - ${update.content_blocks ?? 0} bloc(s), ${update.visual_slots ?? 0} visuel(s)`,
                        "completed"
                    );
                }
                if (update.type === "style_review_completed") {
                    const detail = `${update.style_changes ?? 0} ajustement(s), ${update.review_issues ?? 0} point(s) de revue.`;
                    setStatus(`Controle final: ${detail}`);
                    appendJobTrace("Controle final", detail, "completed");
                }
                if (update.type === "document_label_started") {
                    const prefix = update.cached ? "Label deja disponible" : "Lecture document";
                    setIntakeEvents((current) => [
                        ...current.slice(-10),
                        `${prefix} ${update.index}/${update.total}: ${update.filename ?? ""}`
                    ]);
                    setStatus(`DocumentLabelAgent: ${update.index}/${update.total} - ${update.filename ?? ""}`);
                }
                if (update.type === "document_label_progress") {
                    setStatus(`DocumentLabelAgent: ${update.completed}/${update.total} document(s), ${update.pending} en cours...`);
                }
                if (update.type === "document_label_completed") {
                    const label = update.label;
                    setIntakeEvents((current) => [
                        ...current.slice(-10),
                        `Label ${update.index}/${update.total}: ${label?.filename ?? "document"} - ${label?.status ?? "ok"}`
                    ]);
                    setStatus(`Document labellise: ${label?.filename ?? ""}`);
                }
                if (update.type === "document_label_failed") {
                    setIntakeEvents((current) => [
                        ...current.slice(-10),
                        `Fallback label ${update.index}/${update.total}: ${update.filename ?? "document"}`
                    ]);
                    setStatus(`Fallback DocumentLabelAgent: ${update.filename ?? ""}`);
                }
                if (update.type === "outline_updated") {
                    if (update.sections) {
                        setOutlinePreview(normalizeOutlinePreview(update.sections));
                    }
                    load();
                }
                if (update.type === "outline_refine_started") {
                    setReportLivePhase("planning");
                    setOutlineRunning(true);
                    setStatus(update.message ?? "Modification du plan...");
                }
                if (update.type === "outline_refine_completed") {
                    setOutlineRunning(false);
                    setReportLivePhase("idle");
                    if (update.sections) {
                        setOutlinePreview(normalizeOutlinePreview(update.sections));
                    }
                    setStatus("Plan modifie. Validation requise.");
                    load();
                }
                if (update.type === "review_comment_created") {
                    setStatus("Commentaire de relecture cree.");
                    load();
                }
                if (update.type === "comment_reply_created" && targetsCurrentBranch) {
                    setStatus("Reponse ajoutee au commentaire.");
                    load();
                }
                if (update.type === "report_instruction_routed" && targetsCurrentBranch) {
                    setStatus(`Instruction globale routee vers ${update.section_ids?.length ?? 0} section(s).`);
                    load();
                }
                if (update.type === "report_assistant_message") {
                    if (update.project) {
                        setProject(update.project);
                    }
                    setStatus("Message assistant rapport recu.");
                }
                if (update.type === "report_assistant_threads" || update.type === "report_assistant_reset") {
                    if (update.project) {
                        setProject(update.project);
                    }
                }
                if (update.type === "review_round_completed") {
                    setStatus(update.round?.summary ?? "Round de relecture termine.");
                    load();
                }
                if (update.type === "analysis_completed") {
                    if (reportLivePhaseRef.current !== "writing")
                        setAnalysisRunning(false);
                    if (update.analysis) {
                        applyAnalysisUpdate(update.analysis);
                    }
                    setStatus(reportLivePhaseRef.current === "writing" ? "Préparation terminée, rédaction en cours..." : update.message ?? "Analyse initiale terminée.");
                }
                if (update.type === "clarification_answered") {
                    if (update.project)
                        applyProjectUpdate(update.project);
                    else
                        applyLightProjectState(update.project_state);
                    if (update.analysis)
                        applyAnalysisUpdate(update.analysis, { forceDraft: true });
                    else if (update.question) {
                        setAnalysis((current) => current ? {
                            ...current,
                            clarification_questions: (current.clarification_questions ?? []).map((item) => item.id === update.question.id
                                ? { ...item, ...update.question }
                                : item),
                        } : current);
                        if (Array.isArray(update.pending_questions)) {
                            setClarificationQuestions(update.pending_questions);
                            setActiveClarificationQuestion((current) => {
                                const authoritativeCurrent = current
                                    ? update.pending_questions.find((item) => item.id === current.id)
                                    : null;
                                return authoritativeCurrent ?? update.pending_questions[0] ?? null;
                            });
                        }
                    }
                    setStatus("Precision utilisateur enregistree.");
                }
            }, (error) => {
                console.warn("Flux Guttenberg interrompu:", error);
            }, ({ reconnected }) => {
                if (!reconnected)
                    return;
                setStatus("Flux temps reel retabli; synchronisation de l'etat...");
                load(activeBranchIdRef.current);
            });
            return () => {
                events.close();
                if (stateReloadTimerRef.current !== null) {
                    window.clearTimeout(stateReloadTimerRef.current);
                    stateReloadTimerRef.current = null;
                }
            };
        }, [projectId]);

    useGuttenbergJobPolling({
        projectId,
        job,
        onUpdate: (updated) => {
            applyJobUpdate(updated);
            setStatus(updated.message);
        },
        onTerminal: async (updated) => {
            if (updated.result?.target_type === "image_edit") {
                clearImageProcessingForComment(
                    updated.result.comment_id,
                    updated.result.section_id,
                    updated.result.image_path
                );
            }
            clearLiveSectionsForJob(updated);
            setBusy(false);
            await load();
        },
        onTransientError: () => {
            setStatus("Connexion au job momentanement indisponible; nouvelle tentative automatique...");
        },
    });

    useEffect(() => {
            const activeJobs = Array.isArray(collaboration?.active_jobs) ? collaboration.active_jobs : [];
            const activeCollaborationJob = activeJobs.find((item) => isActiveJob(item)) ?? null;
            if (activeCollaborationJob && !isActiveJob(job)) {
                applyJobUpdate(activeCollaborationJob);
                setBusy(true);
                setStatus(activeCollaborationJob.message || "Operation Guttenberg en cours.");
                return;
            }
            const activeJobLock = (collaboration?.locks ?? []).find((lock) => lock.kind === "ai" && lock.scope === "report" && lock.job_id);
            if (!activeJobLock?.job_id || isActiveJob(job))
                return;
            let cancelled = false;
            api.job(projectId, activeJobLock.job_id)
                .then((updated) => {
                    if (cancelled || !isActiveJob(updated))
                        return;
                    applyJobUpdate(updated);
                    setBusy(true);
                    setStatus(updated.message || activeJobLock.label || "Operation Guttenberg en cours.");
                })
                .catch(() => {});
            return () => {
                cancelled = true;
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [collaboration, job, projectId]);

    useEffect(() => {
            if (!commentMode) {
                setCommentAnchor(null);
            }
        }, [commentMode]);

    useEffect(() => {
            if (!job || !["queued", "running"].includes(job.status))
                return undefined;
            setActivityClock(Date.now());
            const timer = window.setInterval(() => setActivityClock(Date.now()), 1000);
            return () => window.clearInterval(timer);
        }, [job]);

    useEffect(() => {
            if (!project || project.context_integration_status !== "pending" || !visualReviewIsCurrent(analysis)
                || isActiveJob(job) || busy || contextAutoIntegrationRef.current)
                return;
            contextAutoIntegrationRef.current = true;
            api.integrateContext(projectId)
                .then((response) => {
                    if (response.job) {
                        applyJobUpdate(response.job);
                        setBusy(true);
                        setStatus(response.job.message || "Integration des nouveaux inputs en cours.");
                    }
                })
                .catch(() => {})
                .finally(() => {
                    contextAutoIntegrationRef.current = false;
                });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            project?.context_integration_status,
            analysis?.visual_inventory_status,
            analysis?.visual_inventory_corpus_fingerprint,
            analysis?.visual_inventory_source_fingerprint,
            analysis?.visual_user_review_corpus_fingerprint,
            analysis?.visual_user_review_source_fingerprint,
            job?.status,
            busy,
            projectId,
        ]);

    if (!project || !initialTabResolved) {
            return <main className="guttenberg-root shell">Chargement du rapport...</main>;
        }

    return (<GuttenbergProjectView
        acceptSuggestion={acceptSuggestion}
        activeAssistantReview={activeAssistantReview}
        activeBranchId={activeBranchId}
        activeClarificationQuestion={activeClarificationQuestion}
        activeReportAssistantThreadId={activeReportAssistantThreadId}
        activeReportTool={activeReportTool}
        activeVersion={activeVersion}
        activityClock={activityClock}
        aiMode={aiMode}
        aiProvider={aiProvider}
        aiProviderBusy={aiProviderBusy}
        aiProviderInfo={aiProviderInfo}
        analysis={analysis}
        analysisDraft={analysisDraft}
        analysisRunning={analysisRunning}
        analyzeProject={analyzeProject}
        resetAnalysis={resetAnalysis}
        answerClarificationQuestion={answerClarificationQuestion}
        appliedReportAssistantActions={appliedReportAssistantActions}
        applyImageCrop={applyImageCrop}
        applyOpenComments={applyOpenComments}
        applyReportAssistantAction={applyReportAssistantAction}
        approveOutline={approveOutline}
        attributionSaving={attributionSaving}
        autoRetouchImage={autoRetouchImage}
        blurEditableBlock={blurEditableBlock}
        branchName={branchName}
        branchState={branchState}
        browserNotificationPermission={browserNotificationPermission}
        busy={busy}
        cancelCurrentJob={cancelCurrentJob}
        changeAiMode={changeAiMode}
        changeAiProvider={changeAiProvider}
        chatMessage={chatMessage}
        clarificationAnswerBusy={clarificationAnswerBusy}
        clarificationQuestions={clarificationQuestions}
        clearDriveSearch={clearDriveSearch}
        closeAssistantReview={closeAssistantReview}
        collaboration={collaboration}
        collaborationSessionRef={collaborationSessionRef}
        commentAnchor={commentAnchor}
        commentDraft={commentDraft}
        commentMode={commentMode}
        commentReplies={commentReplies}
        contextDraft={contextDraft}
        contextPanelOpen={contextPanelOpen}
        contextSaving={contextSaving}
        contextUploadBusy={contextUploadBusy}
        costs={costs}
        createBranch={createBranch}
        createMilestone={createMilestone}
        createReportSection={createReportSection}
        deletePreviewBlock={deletePreviewBlock}
        deleteReportSection={deleteReportSection}
        dismissComment={dismissComment}
        driveBreadcrumbs={driveBreadcrumbs}
        driveFiles={driveFiles}
        driveLoading={driveLoading}
        driveQuery={driveQuery}
        driveSelection={driveSelection}
        editMode={editMode}
        enableBrowserQuestionNotifications={enableBrowserQuestionNotifications}
        exportFile={exportFile}
        exportRunning={exportRunning}
        fastMode={fastMode}
        fastModeBusy={fastModeBusy}
        fastModeInfo={fastModeInfo}
        focusEditableBlock={focusEditableBlock}
        focusReportComment={focusReportComment}
        generateOutline={generateOutline}
        imageEditTarget={imageEditTarget}
        importSelectedDriveFiles={importSelectedDriveFiles}
        insertManualImageFromComposer={insertManualImageFromComposer}
        insertPreviewBlock={insertPreviewBlock}
        insertSelectedBankImageFromComposer={insertSelectedBankImageFromComposer}
        intakeEvents={intakeEvents}
        isBlockingJob={isBlockingJob}
        isImageEditJob={isImageEditJob}
        job={job}
        jobTrace={jobTrace}
        liveSections={liveSections}
        load={load}
        mainTab={mainTab}
        manualImageCaption={manualImageCaption}
        manualImageFile={manualImageFile}
        manualImageNote={manualImageNote}
        mergeBranch={mergeBranch}
        mergeFeedback={mergeFeedback}
        milestoneLabel={milestoneLabel}
        navigateAssistantReview={navigateAssistantReview}
        openDriveBreadcrumb={openDriveBreadcrumb}
        openDriveFolder={openDriveFolder}
        openImageCropEditor={openImageCropEditor}
        openReportAssistantComposer={openReportAssistantComposer}
        openReportAssistantImageBank={openReportAssistantImageBank}
        outlineApprovalPending={outlineApprovalPending}
        outlineDraft={outlineDraft}
        outlineInstruction={outlineInstruction}
        outlinePreview={outlinePreview}
        outlineRunning={outlineRunning}
        pendingComment={pendingComment}
        prepareVisuals={prepareVisuals}
        processingCommentIds={processingCommentIds}
        processingImageKeys={processingImageKeys}
        processingSectionIds={processingSectionIds}
        project={project}
        projectId={projectId}
        refineOutline={refineOutline}
        refreshDriveFiles={refreshDriveFiles}
        rejectSuggestion={rejectSuggestion}
        replaceImageWithBank={replaceImageWithBank}
        replaceImageWithUpload={replaceImageWithUpload}
        report={report}
        reportAssistantThinking={reportAssistantThinking}
        reportInstruction={reportInstruction}
        reportInstructionBankImages={reportInstructionBankImages}
        reportInstructionFiles={reportInstructionFiles}
        reportInstructionImages={reportInstructionImages}
        reportLivePhase={reportLivePhase}
        reportOutlineCollapsed={reportOutlineCollapsed}
        resetManualImageDraft={resetManualImageDraft}
        resetReportAssistant={resetReportAssistant}
        restoreBlockVersion={restoreBlockVersion}
        restoreMilestone={restoreMilestone}
        restoreSectionVersion={restoreSectionVersion}
        reviewAssistantMessageChanges={reviewAssistantMessageChanges}
        run={run}
        runFinalReview={runFinalReview}
        saveAnalysis={saveAnalysis}
        saveContextPanel={saveContextPanel}
        savePreviewBlock={savePreviewBlock}
        savingSectionIds={savingSectionIds}
        searchDriveFiles={searchDriveFiles}
        selectChartForComment={selectChartForComment}
        selectImageForComment={selectImageForComment}
        selectImageForEdit={selectImageForEdit}
        selectedBankImage={selectedBankImage}
        sendChat={sendChat}
        setActiveClarificationQuestion={setActiveClarificationQuestion}
        setActiveReportTool={setActiveReportTool}
        setActiveVersion={setActiveVersion}
        setBranchName={setBranchName}
        setChatMessage={setChatMessage}
        setCommentAnchor={setCommentAnchor}
        setCommentDraft={setCommentDraft}
        setCommentMode={setCommentMode}
        setCommentReplies={setCommentReplies}
        setContextPanelOpen={setContextPanelOpen}
        setDriveQuery={setDriveQuery}
        setEditMode={setEditMode}
        setImageEditTarget={setImageEditTarget}
        setMainTab={setMainTab}
        setManualImageCaption={setManualImageCaption}
        setManualImageFile={setManualImageFile}
        setManualImageNote={setManualImageNote}
        setMergeFeedback={setMergeFeedback}
        setMilestoneLabel={setMilestoneLabel}
        setOutlineInstruction={setOutlineInstruction}
        setPendingComment={setPendingComment}
        setReportInstruction={setReportInstruction}
        setReportInstructionFiles={setReportInstructionFiles}
        setReportInstructionImages={setReportInstructionImages}
        setReportOutlineCollapsed={setReportOutlineCollapsed}
        setSelectedBankImage={setSelectedBankImage}
        setWorkflowCollapsed={setWorkflowCollapsed}
        startAnchoredComment={startAnchoredComment}
        startBankImageDiscussion={startBankImageDiscussion}
        startGeneration={startGeneration}
        startInsertionComment={startInsertionComment}
        status={status}
        submitAndTreatComment={submitAndTreatComment}
        submitComment={submitComment}
        submitCommentReply={submitCommentReply}
        submitOutlineInstruction={submitOutlineInstruction}
        submitReportInstruction={submitReportInstruction}
        suggestComment={suggestComment}
        suggestOpenComments={suggestOpenComments}
        switchBranch={switchBranch}
        syncBrief={syncBrief}
        targetPages={targetPages}
        titleDraft={titleDraft}
        toggleDriveFile={toggleDriveFile}
        toggleGuttenbergAttribution={toggleGuttenbergAttribution}
        toggleReportAssistantBankImage={toggleReportAssistantBankImage}
        updateAnalysisDraft={updateAnalysisDraft}
        updateContextDraft={updateContextDraft}
        updateTargetPagesDraft={updateTargetPagesDraft}
        updateTextCommentAnchor={updateTextCommentAnchor}
        updateTitleDraft={updateTitleDraft}
        updateVisualAssetLabel={updateVisualAssetLabel}
        updateVisualAssetStatus={updateVisualAssetStatus}
        uploadClientLogo={uploadClientLogo}
        uploadFiles={uploadFiles}
        uploadLiveContextFiles={uploadLiveContextFiles}
        workflowCollapsed={workflowCollapsed}
    />);
}


export {
    GuttenbergReportPage,
    ProjectPage
};

export {
    CostPanel,
    DriveSourcePicker,
    PlanReviewPanel,
    WorkflowStepper,
} from "./GuttenbergWorkflowPanels";
export {
    AnalysisPanel,
    AssetPreview,
    ClarificationQuestionModal,
} from "./GuttenbergAnalysisPanels";
export {
    EditableText,
    FullReportPreview,
    ReportAttributionToggle,
    paginateBlockHeights,
} from "./GuttenbergReportPreview";
export {
    rotateAnnotationPercent,
    rotateCropPercent,
} from "./GuttenbergImageAndDiff";
export {
    ChartPreview,
    capitalizeCaption,
    inlineMarkdownFromEditable,
    markdownTableRow,
    normalizeTableBlock,
    parseMarkdownBlocks,
    serializeMarkdownTable,
    tableBlockToMarkdown,
    tableStructureToMarkdown,
} from "./GuttenbergChartMarkdown";
export {
    mergeDriveFilePage,
    nextClarificationQuestion,
    nextDriveBreadcrumbs,
    reportAssistantChangeTargets,
    shouldAutoGenerateOutline,
    sourceImageExclusionSuggestion,
    uploadSourceBatch,
    visualAssetSourceKey,
    visualReviewIsCurrent,
} from "./GuttenbergPageUtils";
